import * as Sentry from '@sentry/react'

export function initSentry(): void {
  // Only initialize in production or if DSN is configured
  const dsn = process.env.REACT_APP_SENTRY_DSN

  if (!dsn) {
    if (process.env.NODE_ENV === 'production') {
      console.warn('Sentry DSN not configured - error tracking disabled')
    }
    return
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,

    // Performance monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Only send errors in production by default
    enabled: process.env.NODE_ENV === 'production',

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      // User cancellation
      'AbortError',
      // Third-party script errors
      /^Script error\.?$/,
    ],

    // Scrub sensitive data
    beforeSend(event) {
      // Remove any potential PII from error messages
      if (event.exception?.values) {
        event.exception.values.forEach(exception => {
          if (exception.value) {
            // Redact email addresses
            exception.value = exception.value.replace(
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
              '[EMAIL_REDACTED]'
            )
            // Redact API keys
            exception.value = exception.value.replace(
              /sk-[a-zA-Z0-9-]+/g,
              '[API_KEY_REDACTED]'
            )
          }
        })
      }
      return event
    },

    // Add user context when available
    initialScope: {
      tags: {
        app: 'food-at-home',
      },
    },
  })
}

// Helper to set user context after login
export function setSentryUser(userId: string, email?: string): void {
  Sentry.setUser({
    id: userId,
    email: email ? '[REDACTED]' : undefined, // Don't store actual email
  })
}

// Helper to clear user on logout
export function clearSentryUser(): void {
  Sentry.setUser(null)
}

// Helper to capture errors with context
export function captureError(error: Error, context?: Record<string, unknown>): void {
  Sentry.captureException(error, {
    extra: context,
  })
}

// Helper for breadcrumbs
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

// Export Sentry for direct access if needed
export { Sentry }
